import { useContext, useRef, useState } from "react";
import { ScrollerContext, prefersReducedMotion } from "../RoyalMahal/hooks";
import { burst } from "./PetalBurst";
// A pair of painted dholaks: dholak.png with its baked-in checkerboard cut away,
// cropped and compressed (see src/images/rang-utsav/README.md).
import dholakArt from "../../images/rang-utsav/dholak.webp";

/*
 * A dholak pair the guest can play. Tap a drum and it sounds (the left a deep "dha",
 * the right a bright "ta"), thumps, rings ripple off its head, the bol floats up and a
 * few petals fly. Until the first tap the drums bob and their heads pulse, inviting one.
 *
 * Both drums come from one painting: each is a copy of it clipped along the front
 * drum's outline, so each can move on its own. The sounds are synthesised with the Web
 * Audio API, so there is nothing to download.
 */

// The front (left) drum's right-hand outline, top to bottom, in percent of the painting.
const OUTLINE = ["54% 0", "54% 18%", "55.5% 30%", "56.5% 45%", "56.5% 62%", "55.5% 75%", "54% 85%", "53% 100%"];
const SPLIT = 0.555;

const DRUMS = {
  left: {
    clip: `polygon(0 0, ${OUTLINE.join(", ")}, 0 100%)`,
    head: [32, 16], // centre of the drumhead, percent of the painting
    bols: ["Dha", "Dhin", "Dha", "Ge"],
    sound: "bass",
  },
  right: {
    clip: `polygon(${OUTLINE.join(", ")}, 100% 100%, 100% 0)`,
    head: [77, 7],
    bols: ["Ta", "Tin", "Na", "Ta"],
    sound: "treble",
  },
};

// ---- Sound ----
let audio = null;
let noiseBuffer = null;

const getAudio = () => {
  const Context = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!Context) return null;
  if (!audio) audio = new Context();
  if (audio.state === "suspended") audio.resume();
  return audio;
};

const noise = (ac) => {
  if (!noiseBuffer) {
    const length = Math.floor(ac.sampleRate * 0.3);
    noiseBuffer = ac.createBuffer(1, length, ac.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
  }
  const source = ac.createBufferSource();
  source.buffer = noiseBuffer;
  return source;
};

// A sharp attack and an exponential fall to silence
const envelope = (ac, t, peak, decay) => {
  const gain = ac.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(peak, t + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + decay);
  return gain;
};

const strike = (kind) => {
  const ac = getAudio();
  if (!ac) return;
  const t = ac.currentTime + 0.005;
  const out = ac.createGain();
  out.gain.value = 0.6;
  out.connect(ac.destination);

  if (kind === "bass") {
    // Dha: the wide head, a deep thud whose pitch sags as the skin settles
    const body = ac.createOscillator();
    body.type = "sine";
    body.frequency.setValueAtTime(150, t);
    body.frequency.exponentialRampToValueAtTime(58, t + 0.3);
    body.connect(envelope(ac, t, 1, 0.55)).connect(out);
    body.start(t);
    body.stop(t + 0.6);

    const slap = noise(ac);
    const muffle = ac.createBiquadFilter();
    muffle.type = "lowpass";
    muffle.frequency.value = 700;
    slap.connect(muffle).connect(envelope(ac, t, 0.5, 0.06)).connect(out);
    slap.start(t);
    slap.stop(t + 0.08);
  } else {
    // Ta: the small head, a bright crack with a short ring
    const crack = noise(ac);
    const band = ac.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = 2200;
    band.Q.value = 0.9;
    crack.connect(band).connect(envelope(ac, t, 0.9, 0.09)).connect(out);
    crack.start(t);
    crack.stop(t + 0.12);

    const ring = ac.createOscillator();
    ring.type = "triangle";
    ring.frequency.setValueAtTime(460, t);
    ring.frequency.exponentialRampToValueAtTime(400, t + 0.2);
    ring.connect(envelope(ac, t, 0.35, 0.22)).connect(out);
    ring.start(t);
    ring.stop(t + 0.25);
  }
};

// ---- Sights ----
const SQUASH = [
  { transform: "none" },
  { transform: "scale(1.035, 0.95)", offset: 0.25 },
  { transform: "scale(0.99, 1.02)", offset: 0.6 },
  { transform: "none" },
];

// Adds a short-lived element to `layer` at the drumhead; it removes itself.
const flash = (layer, className, [x, y], text) => {
  const el = document.createElement("span");
  el.className = className;
  el.style.left = `${x}%`;
  el.style.top = `${y}%`;
  if (text) el.textContent = text;
  layer.appendChild(el);
  setTimeout(() => el.remove(), 1000);
};

const Dholak = ({ className = "" }) => {
  const scrollerRef = useContext(ScrollerContext);
  const stageRef = useRef(null);
  const fxRef = useRef(null);
  const drumRefs = { left: useRef(null), right: useRef(null) };
  const count = useRef({ left: 0, right: 0, keyboard: 0 });
  const [played, setPlayed] = useState(false);

  const play = (event) => {
    const rect = stageRef.current.getBoundingClientRect();
    // A tap plays the drum under it; Enter / Space (no pointer) take turns between the two
    let side;
    if (event.detail === 0) side = count.current.keyboard++ % 2 ? "right" : "left";
    else side = (event.clientX - rect.left) / rect.width < SPLIT ? "left" : "right";
    const drum = DRUMS[side];
    const bol = drum.bols[count.current[side]++ % drum.bols.length];

    strike(drum.sound);
    setPlayed(true);
    flash(fxRef.current, "ut-dholak__bol", drum.head, bol);
    if (prefersReducedMotion()) return;

    const el = drumRefs[side].current;
    if (el && el.animate) el.animate(SQUASH, { duration: 340, easing: "ease-out" });
    flash(fxRef.current, "ut-dholak__ring", drum.head);
    const host = scrollerRef.current;
    if (host) burst(host, rect.left + (rect.width * drum.head[0]) / 100, rect.top + (rect.height * drum.head[1]) / 100);
  };

  return (
    <div className={`ut-dholak ${played ? "is-played" : ""} ${className}`}>
      <button ref={stageRef} type="button" className="ut-dholak__stage" onClick={play} aria-label="Play the dholak">
        {["right", "left"].map((side) => (
          <img
            key={side}
            ref={drumRefs[side]}
            className={`ut-dholak__drum ut-dholak__drum--${side}`}
            src={dholakArt}
            alt=""
            draggable="false"
            loading="lazy"
            decoding="async"
            style={{ clipPath: DRUMS[side].clip, WebkitClipPath: DRUMS[side].clip }}
          />
        ))}
        {/* Pulses on the drumheads until the first tap */}
        {["left", "right"].map((side) => (
          <span
            key={side}
            className={`ut-dholak__cue ut-dholak__cue--${side}`}
            style={{ left: `${DRUMS[side].head[0]}%`, top: `${DRUMS[side].head[1]}%` }}
            aria-hidden="true"
          />
        ))}
        {/* Rings and bols are added here as the drums are played */}
        <span ref={fxRef} className="ut-dholak__fx" aria-hidden="true" />
      </button>
      {/* <p className="ut-kicker ut-dholak__hint">{played ? "Keep playing!" : "Tap the dholak to join the celebration"}</p> */}
    </div>
  );
};

export default Dholak;
